import { defineStore } from "pinia";
import { MenuItem } from "primevue/menuitem";
import { Component, nextTick, ref } from "vue";
import { Privilege } from "../common/privilege.common";
import { useFacade } from "../facades";

export interface Menu {
    key:
        | "dashboard"
        | "agency"
        | "agency-settings"
        | "agency-user"
        | "device"
        | "attendance-physical-device"
        | "user-device-binding"
        | "user"
        | "group"
        | "schedule"
        | "schedule-global"
        | "schedule-group"
        | "schedule-time-table"
        | "user_location"
        | "attendance"
        | "attendance-history"
        | "attendance-submission"
        | "settings"
        | "settings-firebase"
        | "whatsapp"
        | "devtools"
        | "devtools-database"
        | "devtools-maintenance"
        | "devtools-mobile"
        | "debug"
        | "debug-database";
    label: string;
    icon: string;
    privilege: Privilege.Names | "*";
    route?: string | undefined;
    child?: Menu[] | undefined;
}

export interface PrimeMenu {
    key: string;
    label: string;
    icon: string | undefined;
    iconComponent?: Component;
    route?: string;
    items?: MenuItem[];
}

export const useMenuStore = defineStore("menu", () => {
    const f = useFacade();
    const currentMenu = ref<Menu["key"] | undefined>();

    const checkPrivilege = (p: string) => Array.from(f.profile.privileges).includes(p);

    const buildItem = (menu: Menu) => {
        if (import.meta.env.MODE === "production" && menu.key === "debug") {
            return [];
        }

        if (!checkPrivilege(menu.privilege)) {
            return [];
        }

        let childMenu: Omit<Menu, "privilege">[] | undefined;
        if (menu.child) {
            childMenu = [];
            for (const c of menu.child) {
                const r = buildItem(c);
                if (r.length > 0) {
                    childMenu.push(r[0] as unknown as Omit<Menu, "privilege">);
                }
            }

            if (menu.privilege === "*" && childMenu.length === 0) {
                return [];
            }
        }

        return [
            {
                key: menu.key,
                label: menu.label,
                route: menu.route,
                icon: menu.icon,
                child: childMenu,
            },
        ];
    };

    const menu = ref([
        ...buildItem({
            key: "dashboard",
            label: "Dashboard",
            route: "/",
            icon: "pi pi-home",
            privilege: "*",
        }),
        ...buildItem({
            key: "agency",
            label: "Mitra",
            route: "/agency",
            icon: "pi pi-globe",
            privilege: "admin.access",
        }),
        ...buildItem({
            key: "agency-user",
            label: "Karyawan",
            route: "/agency-user",
            icon: "pi pi-users",
            privilege: "agency_user.access",
        }),
        ...buildItem({
            key: "device",
            label: "Perangkat",
            icon: "pi pi-server",
            privilege: "*",
            child: [
                {
                    key: "attendance-physical-device",
                    label: "Absensi",
                    icon: "pi pi-server",
                    privilege: "device.access",
                    route: "/device",
                },
                {
                    key: "user-device-binding",
                    label: "Pengguna",
                    icon: "pi pi-mobile",
                    privilege: "user_device_binding.access",
                    route: "/user-device-binding",
                },
            ],
        }),
        ...buildItem({
            key: "user",
            label: "Pengguna",
            route: "/user-attendance",
            icon: "pi pi-user",
            privilege: "user_attendance.access",
        }),
        ...buildItem({
            key: "group",
            label: "Grup",
            route: "/group",
            icon: "pi pi-users",
            privilege: "group.access",
        }),
        ...buildItem({
            key: "user_location",
            label: "Lokasi Pengguna",
            route: "/user-location",
            icon: "pi pi-map",
            privilege: "user_location.access",
        }),
        ...buildItem({
            key: "schedule",
            label: "Jadwal",
            icon: "pi pi-calendar",
            privilege: "*",
            child: [
                {
                    key: "schedule-global",
                    label: "Global",
                    icon: "pi pi-calendar",
                    privilege: "schedule_global.access",
                    route: "/schedule-global",
                },
                {
                    key: "schedule-group",
                    label: "Grup",
                    icon: "pi pi-calendar",
                    privilege: "schedule_group.access",
                    route: "/schedule-group",
                },
                {
                    key: "schedule-time-table",
                    label: "Tabel Waktu",
                    icon: "pi pi-clock",
                    privilege: "schedule_time_table.access",
                    route: "/schedule-group-time-table",
                },
            ],
        }),
        ...buildItem({
            key: "attendance",
            label: "Kehadiran",
            icon: "pi pi-list",
            privilege: "*",
            child: [
                {
                    key: "attendance-history",
                    label: "Riwayat",
                    icon: "pi pi-list",
                    privilege: "attendance.access",
                    route: "/attendance",
                },
                {
                    key: "attendance-submission",
                    label: "Pengajuan",
                    icon: "pi pi-list",
                    privilege: "attendance_submission.access",
                    route: "/attendance-submission",
                },
            ],
        }),
        ...buildItem({
            key: "settings",
            label: "Pengaturan",
            icon: "pi pi-cog",
            privilege: "*",
            child: [
                {
                    key: "settings-firebase",
                    label: "Firebase",
                    icon: "pi pi-send",
                    privilege: "settings.access",
                    route: "/settings-firebase",
                },
                {
                    key: "agency-settings",
                    label: "Mitra",
                    icon: "pi pi-cog",
                    privilege: "agency.settings",
                    route: "/agency-settings",
                },
            ],
        }),
        ...buildItem({
            key: "whatsapp",
            label: "Whatsapp Unofficial (3P)",
            route: "/agency-wa",
            icon: "pi pi-whatsapp",
            privilege: "agency.settings",
        }),
        ...buildItem({
            key: "devtools",
            label: "Devtools",
            icon: "pi pi-box",
            privilege: "*",
            child: [
                {
                    key: "devtools-database",
                    label: "Database",
                    icon: "pi pi-database",
                    privilege: "dev.tools",
                    route: "/dev/database",
                },
                {
                    key: "devtools-maintenance",
                    label: "Maintenance",
                    icon: "pi pi-stop",
                    privilege: "dev.tools",
                    route: "/dev/maintenance",
                },
                {
                    key: "devtools-mobile",
                    label: "Mobile",
                    icon: "pi pi-mobile",
                    privilege: "dev.tools",
                    route: "/dev/mobile",
                },
            ],
        }),
        ...buildItem({
            key: "debug",
            label: "Debug Tools",
            icon: "pi pi-box",
            privilege: "*",
            child: [
                {
                    key: "debug-database",
                    label: "Database",
                    icon: "pi pi-database",
                    privilege: "dev.tools",
                    route: "/debug-tools/database",
                },
            ],
        }),
    ]);

    function setActive(key: Menu["key"]) {
        nextTick(() => {
            if (document) {
                const selected = menu.value.filter((f) => f.key === key)[0];
                if (selected) {
                    document.title = `Smart Attend - ${selected.label}`;
                }
            }

            currentMenu.value = key;
        });
    }

    function isActive(menu: Menu["key"]) {
        return currentMenu.value === menu;
    }

    return { currentMenu, menu, setActive, isActive };
});
